import { useState, useEffect, useCallback } from 'react';
import {
  getTestTasks,
  createTestTask,
  updateTestTask,
  deleteTestTask,
  getJiraProjectUsers,
  getJiraPriorities,
  getJiraIssueTypes,
  type TestTask,
  type JiraUser,
  type JiraPriority,
  type JiraIssueType,
  type JiraConfigStatus,
} from '../../services/firebase';
import { Button } from '../common/Button';
import { Modal } from '../common/Modal';
import { Badge } from '../common/Badge';
import toast from 'react-hot-toast';

/**
 * Convert HTML to markdown format.
 */
function htmlToMarkdown(html: string): string {
  // Create a temporary DOM element to parse HTML
  const doc = new DOMParser().parseFromString(html, 'text/html');

  const processNode = (node: Node): string => {
    if (node.nodeType === Node.TEXT_NODE) {
      return node.textContent || '';
    }

    if (node.nodeType !== Node.ELEMENT_NODE) {
      return '';
    }

    const el = node as HTMLElement;
    const tag = el.tagName.toLowerCase();
    const children = Array.from(el.childNodes).map(processNode).join('');

    switch (tag) {
      case 'b':
      case 'strong':
        return `**${children}**`;
      case 'i':
      case 'em':
        return `*${children}*`;
      case 'code':
        return `\`${children}\``;
      case 'pre':
        return `\`\`\`\n${children}\n\`\`\`\n`;
      case 'h1':
        return `# ${children}\n`;
      case 'h2':
        return `## ${children}\n`;
      case 'h3':
        return `### ${children}\n`;
      case 'h4':
        return `#### ${children}\n`;
      case 'h5':
        return `##### ${children}\n`;
      case 'h6':
        return `###### ${children}\n`;
      case 'p':
        return `${children}\n\n`;
      case 'br':
        return '\n';
      case 'ul':
        return children;
      case 'ol':
        return children;
      case 'li': {
        const parent = el.parentElement;
        if (parent?.tagName.toLowerCase() === 'ol') {
          const index = Array.from(parent.children).indexOf(el) + 1;
          return `${index}. ${children.trim()}\n`;
        }
        return `- ${children.trim()}\n`;
      }
      case 'blockquote':
        return children.split('\n').map(line => `> ${line}`).join('\n') + '\n';
      case 'a': {
        const href = el.getAttribute('href');
        return href ? `[${children}](${href})` : children;
      }
      case 'div':
      case 'span':
        return children;
      default:
        return children;
    }
  };

  return processNode(doc.body).trim();
}

/**
 * Handle paste event to convert HTML to markdown.
 */
function handleRichTextPaste(
  e: React.ClipboardEvent<HTMLTextAreaElement>,
  setValue: (value: string) => void,
  currentValue: string
) {
  const html = e.clipboardData.getData('text/html');

  if (html) {
    e.preventDefault();
    const markdown = htmlToMarkdown(html);

    // Insert at cursor position
    const textarea = e.currentTarget;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const newValue = currentValue.substring(0, start) + markdown + currentValue.substring(end);
    setValue(newValue);

    // Set cursor position after inserted text
    setTimeout(() => {
      textarea.selectionStart = textarea.selectionEnd = start + markdown.length;
    }, 0);
  }
}

interface TestingManagerProps {
  jiraConfig: JiraConfigStatus | null;
}

export function TestingManager({ jiraConfig }: TestingManagerProps) {
  const [tasks, setTasks] = useState<TestTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  // Create form state
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [createTitle, setCreateTitle] = useState('');
  const [createBody, setCreateBody] = useState('');
  const [createIssueType, setCreateIssueType] = useState('Task');
  const [createAssignee, setCreateAssignee] = useState('');
  const [createPriority, setCreatePriority] = useState('');
  const [createTag, setCreateTag] = useState('');
  const [customTag, setCustomTag] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  // Tag history from localStorage
  const [recentTags, setRecentTags] = useState<string[]>(() => {
    const saved = localStorage.getItem('testTaskTags');
    return saved ? JSON.parse(saved) : [];
  });

  // Edit modal state
  const [editTask, setEditTask] = useState<TestTask | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [editBody, setEditBody] = useState('');
  const [editAssignee, setEditAssignee] = useState('');
  const [editPriority, setEditPriority] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);

  // Delete confirmation
  const [deleteTask, setDeleteTask] = useState<TestTask | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Jira metadata
  const [users, setUsers] = useState<JiraUser[]>([]);
  const [priorities, setPriorities] = useState<JiraPriority[]>([]);
  const [issueTypes, setIssueTypes] = useState<JiraIssueType[]>([]);
  const [metaLoading, setMetaLoading] = useState(false);

  // Sorting and filtering
  const [sortOrder, setSortOrder] = useState<string>('created DESC');
  const [showClosed, setShowClosed] = useState(false);

  // Pagination
  const [nextPageToken, setNextPageToken] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const PAGE_SIZE = 50;

  const loadTasks = useCallback(async (append = false, pageToken?: string | null, sort?: string, includeClosed?: boolean) => {
    if (append) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }
    try {
      const data = await getTestTasks({
        nextPageToken: append && pageToken ? pageToken : undefined,
        maxResults: PAGE_SIZE,
        sortOrder: sort ?? sortOrder,
        includeClosed: includeClosed ?? showClosed,
      });
      if (append) {
        setTasks(prev => [...prev, ...data.tasks]);
      } else {
        setTasks(data.tasks);
      }
      setNextPageToken(data.nextPageToken);
      setHasMore(data.hasMore);
    } catch (error) {
      console.error('Error loading test tasks:', error);
      toast.error('Failed to load test tasks');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [sortOrder, showClosed]);

  const loadMetadata = useCallback(async () => {
    if (!jiraConfig?.configured) return;
    setMetaLoading(true);
    try {
      const [usersData, prioritiesData, issueTypesData] = await Promise.all([
        getJiraProjectUsers(),
        getJiraPriorities(),
        getJiraIssueTypes(),
      ]);
      setUsers(usersData);
      setPriorities(prioritiesData);
      setIssueTypes(issueTypesData);
    } catch (error) {
      console.error('Error loading Jira metadata:', error);
    } finally {
      setMetaLoading(false);
    }
  }, [jiraConfig?.configured]);

  useEffect(() => {
    if (jiraConfig?.configured) {
      loadTasks();
      loadMetadata();
    }
  }, [jiraConfig?.configured, loadTasks, loadMetadata]);

  const filteredTasks = tasks.filter((task) => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      task.summary.toLowerCase().includes(term) ||
      task.key.toLowerCase().includes(term) ||
      task.status.toLowerCase().includes(term) ||
      task.assignee?.toLowerCase().includes(term)
    );
  });

  // Sanitize tag - Jira labels cannot contain spaces
  const sanitizeTag = (tag: string) => tag.trim().replace(/\s+/g, '-');

  const saveTagToRecent = (tag: string) => {
    if (!tag) return;
    const sanitized = sanitizeTag(tag);
    const updated = [sanitized, ...recentTags.filter(t => t !== sanitized)].slice(0, 20);
    setRecentTags(updated);
    localStorage.setItem('testTaskTags', JSON.stringify(updated));
  };

  const handleAddCustomTag = () => {
    const tag = sanitizeTag(customTag);
    if (tag && !recentTags.includes(tag)) {
      saveTagToRecent(tag);
    }
    setCreateTag(tag);
    setCustomTag('');
  };

  const handleCreate = async () => {
    if (!createTitle.trim()) {
      toast.error('Title is required');
      return;
    }

    // Determine the tag to use
    const tagToUse = createTag || customTag.trim();

    setIsCreating(true);
    try {
      const result = await createTestTask({
        title: createTitle.trim(),
        body: createBody.trim() || undefined,
        issueType: createIssueType || undefined,
        assigneeId: createAssignee || undefined,
        priorityId: createPriority || undefined,
        additionalLabels: tagToUse ? [tagToUse] : undefined,
      });

      // Save the tag to recent if used
      if (tagToUse) {
        saveTagToRecent(tagToUse);
      }

      toast.success(`Created ${result.key}`);
      setShowCreateForm(false);
      setCreateTitle('');
      setCreateBody('');
      setCreateIssueType('Task');
      setCreateAssignee('');
      setCreatePriority('');
      setCreateTag('');
      setCustomTag('');
      loadTasks();
    } catch (error) {
      console.error('Error creating test task:', error);
      toast.error('Failed to create test task');
    } finally {
      setIsCreating(false);
    }
  };

  const handleEdit = (task: TestTask) => {
    setEditTask(task);
    // Remove [TEST] prefix for editing
    setEditTitle(task.summary.replace(/^\[TEST\]\s*/i, ''));
    // Use the description from the task
    setEditBody(task.description || '');
    // Use assigneeId directly if available, otherwise lookup by name
    setEditAssignee(task.assigneeId || '');
    // Use priorityId directly if available, otherwise lookup by name
    setEditPriority(task.priorityId || '');
  };

  const handleUpdate = async () => {
    if (!editTask) return;

    setIsUpdating(true);
    try {
      await updateTestTask({
        issueKey: editTask.key,
        title: editTitle.trim() || undefined,
        body: editBody.trim() || undefined,
        assigneeId: editAssignee || undefined,
        priorityId: editPriority || undefined,
      });
      toast.success('Task updated');
      setEditTask(null);
      loadTasks();
    } catch (error) {
      console.error('Error updating test task:', error);
      toast.error('Failed to update test task');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTask) return;

    setIsDeleting(true);
    try {
      await deleteTestTask(deleteTask.key);
      toast.success('Task deleted');
      setDeleteTask(null);
      loadTasks();
    } catch (error) {
      console.error('Error deleting test task:', error);
      toast.error('Failed to delete test task');
    } finally {
      setIsDeleting(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  if (!jiraConfig?.configured) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 text-center">
        <svg className="w-12 h-12 mx-auto text-amber-500 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
        <h3 className="text-lg font-medium text-amber-800 mb-2">Jira Not Configured</h3>
        <p className="text-amber-700">Configure Jira integration in the settings above to create and manage test tasks.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header with Create Button */}
      <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 sm:items-center">
        <div className="relative w-full sm:flex-1 sm:max-w-md">
          <input
            type="text"
            placeholder="Search test tasks..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-4 py-2 pl-10 focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
          />
          <svg
            className="absolute left-3 top-2.5 h-5 w-5 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
            <input
              type="checkbox"
              checked={showClosed}
              onChange={(e) => {
                setShowClosed(e.target.checked);
                loadTasks(false, null, undefined, e.target.checked);
              }}
              className="rounded border-gray-300 text-brand-600 focus:ring-brand-500"
            />
            Show Closed
          </label>
          <select
            value={sortOrder}
            onChange={(e) => {
              setSortOrder(e.target.value);
              loadTasks(false, null, e.target.value);
            }}
            className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
          >
            <option value="created DESC">Newest First</option>
            <option value="created ASC">Oldest First</option>
            <option value="priority ASC">Priority (High to Low)</option>
            <option value="priority DESC">Priority (Low to High)</option>
            <option value="summary ASC">Title (A-Z)</option>
            <option value="summary DESC">Title (Z-A)</option>
            <option value="status ASC">Status</option>
          </select>
          <button
            onClick={() => loadTasks()}
            disabled={loading}
            className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 hover:text-gray-900 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            <svg className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Refresh
          </button>
          <Button onClick={() => setShowCreateForm(true)}>
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Create Test Task
          </Button>
        </div>
      </div>

      {/* Tasks Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600 mx-auto"></div>
            <p className="text-gray-500 mt-2">Loading test tasks...</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Key</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Title</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tags</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Assignee</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Priority</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredTasks.map((task) => (
                  <tr key={task.key} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm">
                      <a
                        href={task.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 font-medium"
                      >
                        {task.key}
                      </a>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900 max-w-md truncate">
                      {task.summary}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <Badge variant={task.status === 'To Do' ? 'warning' : task.status === 'Done' ? 'success' : 'info'}>
                        {task.status}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {task.labels && task.labels.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {task.labels.map((label) => (
                            <Badge key={label} variant="default">{label}</Badge>
                          ))}
                        </div>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {task.assignee ? (
                        <div className="flex items-center gap-2">
                          {task.assigneeAvatar && (
                            <img src={task.assigneeAvatar} alt="" className="w-5 h-5 rounded-full" />
                          )}
                          {task.assignee}
                        </div>
                      ) : (
                        <span className="text-gray-400">Unassigned</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {task.priority ? (
                        <div className="flex items-center gap-1">
                          {task.priorityIcon && (
                            <img src={task.priorityIcon} alt="" className="w-4 h-4" />
                          )}
                          {task.priority}
                        </div>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">
                      {formatDate(task.created)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-2">
                        <Button variant="ghost" size="sm" onClick={() => handleEdit(task)}>
                          Edit
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => setDeleteTask(task)} className="text-red-600 hover:text-red-700">
                          Delete
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {!loading && filteredTasks.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            {searchTerm ? 'No tasks match your search' : 'No test tasks yet. Create one to get started!'}
          </div>
        )}
        {/* Load More */}
        {!loading && hasMore && !searchTerm && (
          <div className="border-t border-gray-200 px-4 py-3 text-center">
            <button
              onClick={() => loadTasks(true, nextPageToken)}
              disabled={loadingMore}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-brand-600 hover:text-brand-700 disabled:opacity-50"
            >
              {loadingMore ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Loading...
                </>
              ) : (
                <>Load More (showing {tasks.length})</>
              )}
            </button>
          </div>
        )}
      </div>
      <p className="text-sm text-gray-500">
        Showing {filteredTasks.length} test task{filteredTasks.length !== 1 ? 's' : ''}
        {hasMore && ' (more available)'}
        {searchTerm && ` (filtered)`}
      </p>

      {/* Create Modal */}
      <Modal isOpen={showCreateForm} onClose={() => setShowCreateForm(false)} title="Create Test Task" size="lg">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Title <span className="text-red-500">*</span>
            </label>
            <div className="flex">
              <span className="inline-flex items-center px-3 rounded-l-lg border border-r-0 border-gray-300 bg-gray-50 text-gray-500 text-sm">
                [TEST]
              </span>
              <input
                type="text"
                value={createTitle}
                onChange={(e) => setCreateTitle(e.target.value)}
                placeholder="Verification code - partial entry"
                className="flex-1 border border-gray-300 rounded-r-lg px-3 py-2 focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
              <span className="font-normal text-gray-500 ml-2">(supports markdown, paste rich text/HTML)</span>
            </label>
            <textarea
              value={createBody}
              onChange={(e) => setCreateBody(e.target.value)}
              onPaste={(e) => handleRichTextPaste(e, setCreateBody, createBody)}
              rows={4}
              placeholder="Given I am on the Verification Code step&#10;When I enter only 3 digits (e.g. &quot;123&quot;)&#10;Then the &quot;Verify&quot; button should be disabled (requires exactly 6 digits)"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
            />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Issue Type</label>
              <select
                value={createIssueType}
                onChange={(e) => setCreateIssueType(e.target.value)}
                disabled={metaLoading}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-500 focus:border-brand-500 disabled:opacity-50"
              >
                {issueTypes.length === 0 && <option value="Task">Task</option>}
                {issueTypes.map((type) => (
                  <option key={type.id} value={type.name}>{type.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Assignee</label>
              <select
                value={createAssignee}
                onChange={(e) => setCreateAssignee(e.target.value)}
                disabled={metaLoading}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-500 focus:border-brand-500 disabled:opacity-50"
              >
                <option value="">Unassigned</option>
                {users.map((user) => (
                  <option key={user.accountId} value={user.accountId}>{user.displayName}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
              <select
                value={createPriority}
                onChange={(e) => setCreatePriority(e.target.value)}
                disabled={metaLoading}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-500 focus:border-brand-500 disabled:opacity-50"
              >
                <option value="">Default</option>
                {priorities.map((priority) => (
                  <option key={priority.id} value={priority.id}>{priority.name}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Additional Tag <span className="font-normal text-gray-500">(optional - for categorizing by area)</span>
            </label>
            <div className="flex gap-2">
              <select
                value={createTag}
                onChange={(e) => {
                  setCreateTag(e.target.value);
                  if (e.target.value) setCustomTag('');
                }}
                className="flex-1 border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
              >
                <option value="">No additional tag</option>
                {recentTags.map((tag) => (
                  <option key={tag} value={tag}>{tag}</option>
                ))}
              </select>
              <div className="flex">
                <input
                  type="text"
                  value={customTag}
                  onChange={(e) => {
                    setCustomTag(e.target.value);
                    if (e.target.value) setCreateTag('');
                  }}
                  placeholder="Or enter new tag..."
                  className="border border-gray-300 rounded-l-lg px-3 py-2 focus:ring-2 focus:ring-brand-500 focus:border-brand-500 w-40"
                />
                <button
                  type="button"
                  onClick={handleAddCustomTag}
                  disabled={!customTag.trim()}
                  className="px-3 py-2 bg-gray-100 border border-l-0 border-gray-300 rounded-r-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Add
                </button>
              </div>
            </div>
            {(createTag || customTag) && (() => {
              const rawTag = createTag || customTag;
              const sanitizedTag = rawTag.replace(/\s+/g, '-');
              const hasSpaces = rawTag !== sanitizedTag;
              return (
                <p className="text-xs text-gray-500 mt-1">
                  Task will have labels: <Badge variant="info">Test</Badge> <Badge variant="default">{sanitizedTag}</Badge>
                  {hasSpaces && <span className="text-amber-600 ml-2">(spaces converted to hyphens)</span>}
                </p>
              );
            })()}
          </div>

          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-sm text-gray-600">
            <strong>Note:</strong> All tasks will have the <Badge variant="info">Test</Badge> label automatically.
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button variant="secondary" onClick={() => setShowCreateForm(false)}>Cancel</Button>
            <Button onClick={handleCreate} disabled={isCreating || !createTitle.trim()}>
              {isCreating ? 'Creating...' : 'Create Task'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Edit Modal */}
      <Modal isOpen={!!editTask} onClose={() => setEditTask(null)} title={`Edit ${editTask?.key || ''}`} size="lg">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
            <div className="flex">
              <span className="inline-flex items-center px-3 rounded-l-lg border border-r-0 border-gray-300 bg-gray-50 text-gray-500 text-sm">
                [TEST]
              </span>
              <input
                type="text"
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                className="flex-1 border border-gray-300 rounded-r-lg px-3 py-2 focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
              />
            </div>
          </div>

          {editTask?.labels && editTask.labels.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tags</label>
              <div className="flex flex-wrap gap-1">
                {editTask.labels.map((label) => (
                  <Badge key={label} variant="default">{label}</Badge>
                ))}
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
              <span className="font-normal text-gray-500 ml-2">(supports markdown, paste rich text/HTML)</span>
            </label>
            <textarea
              value={editBody}
              onChange={(e) => setEditBody(e.target.value)}
              onPaste={(e) => handleRichTextPaste(e, setEditBody, editBody)}
              rows={4}
              placeholder="Enter description..."
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Assignee</label>
              <select
                value={editAssignee}
                onChange={(e) => setEditAssignee(e.target.value)}
                disabled={metaLoading}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-500 focus:border-brand-500 disabled:opacity-50"
              >
                <option value="">Unassigned</option>
                {users.map((user) => (
                  <option key={user.accountId} value={user.accountId}>{user.displayName}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
              <select
                value={editPriority}
                onChange={(e) => setEditPriority(e.target.value)}
                disabled={metaLoading}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-500 focus:border-brand-500 disabled:opacity-50"
              >
                <option value="">Default</option>
                {priorities.map((priority) => (
                  <option key={priority.id} value={priority.id}>{priority.name}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button variant="secondary" onClick={() => setEditTask(null)}>Cancel</Button>
            <Button onClick={handleUpdate} disabled={isUpdating}>
              {isUpdating ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={!!deleteTask} onClose={() => setDeleteTask(null)} title="Delete Test Task" size="sm">
        <div className="space-y-4">
          <p className="text-gray-700">
            Are you sure you want to delete <strong>{deleteTask?.key}</strong>? This will permanently remove the task from Jira.
          </p>
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button variant="secondary" onClick={() => setDeleteTask(null)}>Cancel</Button>
            <Button variant="danger" onClick={handleDelete} disabled={isDeleting}>
              {isDeleting ? 'Deleting...' : 'Delete'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
